import React, { useState } from 'react';
import { Reservation } from '../../interfaces';
import {
    RESERVATION_STATUS_META,
    WAITLIST_CALLED_GRACE_MINUTES,
    waitStatus,
} from '../../hooks/useReservations';

interface Props {
    entry: Reservation;
    /** Place in the queue, 1 for the party who has waited longest. */
    position: number;
    /** Shared clock from the board, so every wait counts up together. */
    now: number;
    isActive: boolean;
    onSelect: (r: Reservation) => void;
    /** Give them a table: hands over to the same assign flow bookings use. */
    onSeat: (r: Reservation) => void;
    onCall: (r: Reservation) => void;
    onEdit: (r: Reservation) => void;
    onRequote: (r: Reservation, minutes: number) => void;
    onLeave: (r: Reservation) => void;
}

/**
 * Telling a waiting party their table is ready is a real action on a real system,
 * but this one has no way to reach them yet — no call, no SMS — so a button that
 * only records "somebody told them" promises more than it does. Off until there
 * is a channel behind it; the state it sets (`notifiedAt`) and everything that
 * reads it stays in place, so this is the only line to change when messaging lands.
 */
const WAITLIST_CALL_ENABLED: boolean = false;

const icons = {
    people: <svg viewBox='0 0 24 24' width='12' height='12' fill='none'><path d='M16 19v-1a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v1M9 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM22 19v-1a4 4 0 0 0-3-3.87M16 5.13A4 4 0 0 1 16 13' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' /></svg>,
    clock: <svg viewBox='0 0 24 24' width='12' height='12' fill='none'><circle cx='12' cy='12' r='9' stroke='currentColor' strokeWidth='1.8' /><path d='M12 7v5l3 2' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' /></svg>,
    phone: <svg viewBox='0 0 24 24' width='12' height='12' fill='none'><path d='M15.5 21A13.5 13.5 0 0 1 3 8.5 3 3 0 0 1 6 5.5h1.6a1 1 0 0 1 1 .8l.7 3a1 1 0 0 1-.5 1.1l-1.2.7a10 10 0 0 0 4.3 4.3l.7-1.2a1 1 0 0 1 1.1-.5l3 .7a1 1 0 0 1 .8 1V17a3 3 0 0 1-3 3Z' stroke='currentColor' strokeWidth='1.7' strokeLinecap='round' strokeLinejoin='round' /></svg>,
    note: <svg viewBox='0 0 24 24' width='12' height='12' fill='none'><path d='M8 6h8M8 10h8M8 14h5M5 3h14v18l-7-3-7 3V3Z' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' /></svg>,
    bell: <svg viewBox='0 0 24 24' width='12' height='12' fill='none'><path d='M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' /></svg>,
};

const fmt = (m: number) => (m < 60 ? `${m}m` : `${Math.floor(m / 60)}h ${m % 60}m`);

/** Fresh quotes offered when a wait has to be extended. */
const REQUOTE_OPTIONS = [5, 10, 15, 20, 30];

const WaitlistCard: React.FC<Props> = ({
    entry: r,
    position,
    now,
    isActive,
    onSelect,
    onSeat,
    onCall,
    onEdit,
    onRequote,
    onLeave,
}) => {
    const [requoting, setRequoting] = useState(false);
    const w = waitStatus(r, now);
    const guestName = `${r.guest?.firstName || 'Guest'}${r.guest?.lastName ? ` ${r.guest.lastName}` : ''}`;
    /**
     * Seeded or historic entries can carry a call that nobody could have made
     * here, and a card must not claim otherwise — so while the action is off, the
     * card reads as though it never happened and shows the wait instead.
     */
    const called = WAITLIST_CALL_ENABLED && w.called;
    const overdue = called && w.calledAgo > WAITLIST_CALLED_GRACE_MINUTES;

    const classes = [
        'resv-wait',
        isActive ? 'is-active' : '',
        called ? 'is-called' : '',
        w.over > 0 && !called ? 'is-over' : '',
    ].filter(Boolean).join(' ');

    return (
        <div
            className={classes}
            style={{ ['--accent' as any]: RESERVATION_STATUS_META.waitlist.accent }}
            role='button'
            tabIndex={0}
            onClick={() => onSelect(r)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(r); } }}
        >
            <span className='resv-wait__bar' />

            <div className='resv-wait__top'>
                <span className='resv-wait__pos' title={`Number ${position} in the queue`}>{position}</span>
                <span className='resv-wait__name'>{guestName}</span>
                <span className='resv-wait__party'>{icons.people}{r.partySize}</span>
            </div>

            <div className='resv-wait__timing'>
                <span className='resv-wait__waited' title={`Joined the queue at ${r.time}`}>
                    {icons.clock}{w.waited === 0 ? 'Just joined' : `Waiting ${fmt(w.waited)}`}
                </span>
                {/* The promise is named, not left in a tooltip: a host being asked
                  * "how much longer?" needs what this party was told, and a tablet
                  * has nothing to hover with. Once they have been called the quote
                  * is history — what matters is whether they turned up, which the
                  * line below says. */}
                {!called && (w.over > 0 ? (
                    <span className='tv-overpill'>
                        {w.quoted}m quote · +{fmt(w.over)} over
                    </span>
                ) : (
                    <span className='resv-wait__quote'>
                        {w.quoted}m quote · {w.remaining === 0 ? 'up now' : `~${fmt(w.remaining)} to go`}
                    </span>
                ))}
            </div>

            {called && (
                <div className={`resv-wait__called${overdue ? ' is-late' : ''}`}>
                    {icons.bell}
                    <span>
                        {w.calledAgo === 0 ? 'Called just now' : `Called ${fmt(w.calledAgo)} ago`}
                        {overdue ? ' — still not here' : ''}
                    </span>
                </div>
            )}

            {r.guest?.phone && (
                <div className='resv-wait__phone'>{icons.phone}<span>{r.guest.phone}</span></div>
            )}

            {r.guestNotes && (
                <div className='resv-wait__note'>{icons.note}<span>{r.guestNotes}</span></div>
            )}

            {isActive && (
                <div className='resv-card__actions' onClick={(e) => e.stopPropagation()}>
                    {requoting ? (
                        <>
                            <span className='resv-wait__requotelabel'>Another</span>
                            {REQUOTE_OPTIONS.map(m => (
                                <button
                                    key={m}
                                    type='button'
                                    className='resv-btn resv-btn--ghost resv-btn--tight'
                                    onClick={() => { onRequote(r, m); setRequoting(false); }}
                                >{m}m</button>
                            ))}
                            <button
                                type='button'
                                className='resv-btn resv-btn--ghost'
                                onClick={() => setRequoting(false)}
                            >Cancel</button>
                        </>
                    ) : (
                        <>
                            <button type='button' className='resv-btn resv-btn--primary' onClick={() => onSeat(r)}>
                                Seat
                            </button>
                            {WAITLIST_CALL_ENABLED && (
                                <button
                                    type='button'
                                    className='resv-btn resv-btn--ghost'
                                    onClick={() => onCall(r)}
                                >{called ? 'Call again' : 'Table ready'}</button>
                            )}
                            <button
                                type='button'
                                className='resv-btn resv-btn--ghost'
                                onClick={() => setRequoting(true)}
                            >Re-quote</button>
                            <button
                                type='button'
                                className='resv-btn resv-btn--ghost'
                                onClick={() => onEdit(r)}
                            >Edit</button>
                            <button
                                type='button'
                                className='resv-btn resv-btn--danger'
                                onClick={() => onLeave(r)}
                            >Left</button>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default WaitlistCard;
